import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Save,
  Type,
  Image,
  Link as LinkIcon,
  Home,
  Users,
  MessageSquare,
  Phone,
  Sparkles,
  Globe,
  DollarSign,
  Brain,
  Layout,
  RefreshCw,
  CheckCircle,
  Star,
  Heart,
  Mail,
  MapPin
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Content sections organized by page/area
const contentSections = {
  // Landing Page - Hero Section
  hero: {
    label: 'Hero Section',
    icon: Home,
    fields: [
      { key: 'tagline', label: 'Tagline (above title)', type: 'text', placeholder: 'Your Journey to Freedom Starts Here' },
      { key: 'title_line1', label: 'Title Line 1', type: 'text', placeholder: 'Transform Your Life,' },
      { key: 'title_line2', label: 'Title Line 2 (highlighted)', type: 'text', placeholder: 'Embrace Freedom' },
      { key: 'subtitle', label: 'Subtitle/Description', type: 'textarea', placeholder: 'Discover the path to financial peace, mental clarity...' },
      { key: 'cta_primary', label: 'Primary Button Text', type: 'text', placeholder: 'Start Your Journey' },
      { key: 'cta_secondary', label: 'Secondary Button Text', type: 'text', placeholder: 'Watch Introduction' },
      { key: 'background_image', label: 'Hero Background Image URL', type: 'url', placeholder: 'https://...' },
    ]
  },

  // Landing Page - Stats
  stats: {
    label: 'Statistics Section',
    icon: Star,
    fields: [
      { key: 'stat1_value', label: 'Stat 1 Value', type: 'text', placeholder: '10K+' },
      { key: 'stat1_label', label: 'Stat 1 Label', type: 'text', placeholder: 'Lives Transformed' },
      { key: 'stat2_value', label: 'Stat 2 Value', type: 'text', placeholder: '50+' },
      { key: 'stat2_label', label: 'Stat 2 Label', type: 'text', placeholder: 'Countries Reached' },
      { key: 'stat3_value', label: 'Stat 3 Value', type: 'text', placeholder: '98%' },
      { key: 'stat3_label', label: 'Stat 3 Label', type: 'text', placeholder: 'Client Satisfaction' },
      { key: 'stat4_value', label: 'Stat 4 Value', type: 'text', placeholder: '500+' },
      { key: 'stat4_label', label: 'Stat 4 Label', type: 'text', placeholder: 'Coaching Hours' },
    ]
  },

  // Landing Page - Freedoms Section
  freedoms: {
    label: 'Three Freedoms Section',
    icon: Sparkles,
    fields: [
      { key: 'section_title', label: 'Section Title', type: 'text', placeholder: 'Your Path to Complete Freedom' },
      { key: 'section_subtitle', label: 'Section Subtitle', type: 'textarea', placeholder: 'We guide you through three interconnected pillars...' },
      // Financial Freedom
      { key: 'financial_title', label: 'Financial Freedom - Title', type: 'text', placeholder: 'Financial Freedom' },
      { key: 'financial_description', label: 'Financial Freedom - Description', type: 'textarea', placeholder: 'Break free from financial stress...' },
      { key: 'financial_feature1', label: 'Financial Freedom - Feature 1', type: 'text', placeholder: 'Debt elimination strategies' },
      { key: 'financial_feature2', label: 'Financial Freedom - Feature 2', type: 'text', placeholder: 'Passive income building' },
      { key: 'financial_feature3', label: 'Financial Freedom - Feature 3', type: 'text', placeholder: 'Smart investment basics' },
      { key: 'financial_image', label: 'Financial Freedom - Image URL', type: 'url', placeholder: 'https://...' },
      // Mental Freedom
      { key: 'mental_title', label: 'Mental Freedom - Title', type: 'text', placeholder: 'Mental Freedom' },
      { key: 'mental_description', label: 'Mental Freedom - Description', type: 'textarea', placeholder: 'Quiet the noise...' },
      { key: 'mental_feature1', label: 'Mental Freedom - Feature 1', type: 'text', placeholder: 'Mindfulness practices' },
      { key: 'mental_feature2', label: 'Mental Freedom - Feature 2', type: 'text', placeholder: 'Stress management' },
      { key: 'mental_feature3', label: 'Mental Freedom - Feature 3', type: 'text', placeholder: 'Emotional resilience' },
      { key: 'mental_image', label: 'Mental Freedom - Image URL', type: 'url', placeholder: 'https://...' },
      // Physical Freedom
      { key: 'physical_title', label: 'Physical Freedom - Title', type: 'text', placeholder: 'Physical Freedom' },
      { key: 'physical_description', label: 'Physical Freedom - Description', type: 'textarea', placeholder: 'Move without limits...' },
      { key: 'physical_feature1', label: 'Physical Freedom - Feature 1', type: 'text', placeholder: 'Flexible fitness routines' },
      { key: 'physical_feature2', label: 'Physical Freedom - Feature 2', type: 'text', placeholder: 'Nutrition guidance' },
      { key: 'physical_feature3', label: 'Physical Freedom - Feature 3', type: 'text', placeholder: 'Location independence' },
      { key: 'physical_image', label: 'Physical Freedom - Image URL', type: 'url', placeholder: 'https://...' },
    ]
  },

  // Landing Page - Testimonials
  testimonials: {
    label: 'Testimonials Section',
    icon: MessageSquare,
    fields: [
      { key: 'section_title', label: 'Section Title', type: 'text', placeholder: 'Stories of Transformation' },
      { key: 'section_subtitle', label: 'Section Subtitle', type: 'textarea', placeholder: 'Hear from people who have embraced freedom...' },
      // Testimonial 1
      { key: 'testimonial1_name', label: 'Testimonial 1 - Name', type: 'text', placeholder: 'Sarah Mitchell' },
      { key: 'testimonial1_role', label: 'Testimonial 1 - Role', type: 'text', placeholder: 'Digital Nomad' },
      { key: 'testimonial1_content', label: 'Testimonial 1 - Content', type: 'textarea', placeholder: 'Nspire Living Healthy transformed...' },
      { key: 'testimonial1_avatar', label: 'Testimonial 1 - Avatar URL', type: 'url', placeholder: 'https://...' },
      // Testimonial 2
      { key: 'testimonial2_name', label: 'Testimonial 2 - Name', type: 'text', placeholder: 'Marcus Chen' },
      { key: 'testimonial2_role', label: 'Testimonial 2 - Role', type: 'text', placeholder: 'Entrepreneur' },
      { key: 'testimonial2_content', label: 'Testimonial 2 - Content', type: 'textarea', placeholder: 'The holistic approach here...' },
      { key: 'testimonial2_avatar', label: 'Testimonial 2 - Avatar URL', type: 'url', placeholder: 'https://...' },
      // Testimonial 3 (optional)
      { key: 'testimonial3_name', label: 'Testimonial 3 - Name (optional)', type: 'text', placeholder: '' },
      { key: 'testimonial3_role', label: 'Testimonial 3 - Role', type: 'text', placeholder: '' },
      { key: 'testimonial3_content', label: 'Testimonial 3 - Content', type: 'textarea', placeholder: '' },
      { key: 'testimonial3_avatar', label: 'Testimonial 3 - Avatar URL', type: 'url', placeholder: '' },
    ]
  },

  // Landing Page - CTA Section
  cta: {
    label: 'Call-to-Action Section',
    icon: Sparkles,
    fields: [
      { key: 'title', label: 'CTA Title', type: 'text', placeholder: 'Ready to Transform Your Life?' },
      { key: 'subtitle', label: 'CTA Subtitle', type: 'textarea', placeholder: 'Join thousands who have found freedom...' },
      { key: 'button_text', label: 'Button Text', type: 'text', placeholder: 'Begin Your Transformation' },
      { key: 'background_image', label: 'CTA Background Image URL', type: 'url', placeholder: 'https://...' },
    ]
  },

  // Coaching Page
  coaching: {
    label: 'Coaching Page',
    icon: Phone,
    fields: [
      { key: 'page_title', label: 'Page Title', type: 'text', placeholder: 'Personal Coaching Sessions' },
      { key: 'page_subtitle', label: 'Page Subtitle', type: 'textarea', placeholder: 'Book one-on-one sessions with our expert coaches...' },
      { key: 'phone_title', label: 'Phone Session Title', type: 'text', placeholder: 'Phone Call' },
      { key: 'phone_description', label: 'Phone Session Description', type: 'text', placeholder: 'One-on-one phone coaching session' },
      { key: 'chat_title', label: 'Chat Session Title', type: 'text', placeholder: 'Chat Session' },
      { key: 'chat_description', label: 'Chat Session Description', type: 'text', placeholder: 'Text-based coaching conversation' },
      { key: 'video_title', label: 'Video Session Title', type: 'text', placeholder: 'Video Call' },
      { key: 'video_description', label: 'Video Session Description', type: 'text', placeholder: 'Face-to-face video coaching' },
      { key: 'booking_cta', label: 'Booking CTA Text', type: 'text', placeholder: 'Book Your Session' },
    ]
  },

  // Media Page
  media: {
    label: 'Media Library Page',
    icon: Layout,
    fields: [
      { key: 'page_title', label: 'Page Title', type: 'text', placeholder: 'Media Library' },
      { key: 'page_subtitle', label: 'Page Subtitle', type: 'textarea', placeholder: 'Explore our collection of videos, lectures, and resources...' },
      { key: 'empty_state_title', label: 'Empty State Title', type: 'text', placeholder: 'No media available yet' },
      { key: 'empty_state_text', label: 'Empty State Text', type: 'text', placeholder: 'Check back soon for new content!' },
    ]
  },

  // Apps Page
  apps: {
    label: 'Apps Page',
    icon: Globe,
    fields: [
      { key: 'page_title', label: 'Page Title', type: 'text', placeholder: 'Our Apps' },
      { key: 'page_subtitle', label: 'Page Subtitle', type: 'textarea', placeholder: 'Download our apps to continue your journey...' },
      { key: 'empty_state_title', label: 'Empty State Title', type: 'text', placeholder: 'No apps available yet' },
      { key: 'empty_state_text', label: 'Empty State Text', type: 'text', placeholder: 'Stay tuned for our upcoming releases!' },
    ]
  },

  // Audio Page
  audio: {
    label: 'Audio Library Page',
    icon: MessageSquare,
    fields: [
      { key: 'page_title', label: 'Page Title', type: 'text', placeholder: 'Audio Library' },
      { key: 'page_subtitle', label: 'Page Subtitle', type: 'textarea', placeholder: 'Listen to meditations, affirmations, relaxing sounds...' },
      { key: 'empty_state_title', label: 'Empty State Title', type: 'text', placeholder: 'No audio found' },
      { key: 'empty_state_text', label: 'Empty State Text', type: 'text', placeholder: 'Check back later for new content' },
    ]
  },

  // Footer
  footer: {
    label: 'Footer',
    icon: Layout,
    fields: [
      { key: 'tagline', label: 'Footer Tagline', type: 'textarea', placeholder: 'Transform your life through holistic coaching...' },
      { key: 'email', label: 'Contact Email', type: 'text', placeholder: 'hello@nspireliving.com' },
      { key: 'phone', label: 'Contact Phone', type: 'text', placeholder: '+1 (234) 567-890' },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Anywhere, Everywhere' },
      { key: 'copyright_text', label: 'Copyright Text', type: 'text', placeholder: 'Nspire Living Healthy. All rights reserved.' },
      { key: 'made_with_text', label: 'Made With Text', type: 'text', placeholder: 'Made with love for a healthier world' },
    ]
  },

  // Site General
  general: {
    label: 'General Site Info',
    icon: Globe,
    fields: [
      { key: 'site_name', label: 'Site Name', type: 'text', placeholder: 'Nspire Living Healthy' },
      { key: 'site_description', label: 'Site Description (SEO)', type: 'textarea', placeholder: 'A lifestyle coaching platform for financial, mental, and physical freedom' },
      { key: 'logo_text', label: 'Logo Text', type: 'text', placeholder: 'N' },
    ]
  },
};

export default function AdminContent() {
  const { currentAdminTheme } = useTheme();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState('hero');
  const [hasChanges, setHasChanges] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data || {});
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (section, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(prev => ({ ...prev, [section]: true }));
  };

  const handleSave = async (section) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      const sectionContent = content[section] || {};
      const fields = contentSections[section]?.fields || [];
      
      // Save each field in the section
      for (const field of fields) {
        const value = sectionContent[field.key] || '';
        await axios.put(`${API}/admin/content`, 
          { section, key: field.key, value },
          { withCredentials: true }
        );
      }
      
      toast.success(`${contentSections[section]?.label || section} saved successfully!`);
      setHasChanges(prev => ({ ...prev, [section]: false }));
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleSaveAll = async () => {
    const sectionsToSave = Object.keys(hasChanges).filter(s => hasChanges[s]);
    if (sectionsToSave.length === 0) {
      toast.info('No changes to save');
      return;
    }

    for (const section of sectionsToSave) {
      await handleSave(section);
    }
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case 'url': return LinkIcon;
      case 'textarea': return FileText;
      default: return Type;
    }
  };

  const renderField = (section, field) => {
    const Icon = getFieldIcon(field.type);
    const value = content[section]?.[field.key] || '';

    return (
      <div key={field.key} className="space-y-2">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Icon className="w-4 h-4 opacity-50" />
          {field.label}
        </Label>
        {field.type === 'textarea' ? (
          <Textarea
            value={value}
            onChange={(e) => handleChange(section, field.key, e.target.value)}
            rows={3}
            placeholder={field.placeholder}
            className="font-body"
            data-testid={`content-${section}-${field.key}`}
          />
        ) : (
          <Input
            type={field.type === 'url' ? 'url' : 'text'}
            value={value}
            onChange={(e) => handleChange(section, field.key, e.target.value)}
            placeholder={field.placeholder}
            className="font-body"
            data-testid={`content-${section}-${field.key}`}
          />
        )}
        {field.type === 'url' && value && (
          <div className="mt-2">
            <img 
              src={value} 
              alt="Preview" 
              className="h-20 w-auto rounded-lg object-cover border"
              style={{ borderColor: currentAdminTheme.border }}
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
        )}
      </div>
    );
  };

  // Group sections by category
  const sectionGroups = {
    'Landing Page': ['hero', 'stats', 'freedoms', 'testimonials', 'cta'],
    'Other Pages': ['coaching', 'media', 'apps', 'audio'],
    'Site Wide': ['footer', 'general'],
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>
              Content Management
            </h1>
            <p className="opacity-70 mt-1">
              Edit all text, images, and content across your website
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchContent}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              onClick={handleSaveAll}
              className="gap-2"
              style={{ backgroundColor: currentAdminTheme.primary }}
              disabled={!Object.values(hasChanges).some(Boolean)}
              data-testid="save-all-content-btn"
            >
              <Save className="w-4 h-4" />
              Save All Changes
            </Button>
          </div>
        </div>

        {/* Content Tabs */}
        <div 
          className="rounded-2xl overflow-hidden"
          style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Tab Navigation */}
            <div className="border-b overflow-x-auto" style={{ borderColor: currentAdminTheme.border }}>
              <TabsList className="w-full justify-start bg-transparent rounded-none h-auto p-0">
                {Object.entries(sectionGroups).map(([groupName, sections]) => (
                  <div key={groupName} className="flex items-center">
                    <span className="px-4 py-3 text-xs font-semibold opacity-50 uppercase tracking-wider">
                      {groupName}
                    </span>
                    {sections.map(sectionKey => {
                      const section = contentSections[sectionKey];
                      const Icon = section.icon;
                      return (
                        <TabsTrigger 
                          key={sectionKey} 
                          value={sectionKey}
                          className="relative px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-current gap-2"
                          style={{ 
                            color: activeTab === sectionKey ? currentAdminTheme.primary : undefined 
                          }}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="hidden sm:inline">{section.label}</span>
                          {hasChanges[sectionKey] && (
                            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-500" />
                          )}
                        </TabsTrigger>
                      );
                    })}
                    <div className="w-px h-8 mx-2" style={{ backgroundColor: currentAdminTheme.border }} />
                  </div>
                ))}
              </TabsList>
            </div>

            {/* Tab Content */}
            {Object.keys(contentSections).map(sectionKey => {
              const section = contentSections[sectionKey];
              const Icon = section.icon;
              
              return (
                <TabsContent key={sectionKey} value={sectionKey} className="p-6 m-0">
                  <div className="max-w-3xl">
                    {/* Section Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: currentAdminTheme.primary }} />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold" style={{ color: currentAdminTheme.foreground }}>
                            {section.label}
                          </h2>
                          <p className="text-sm opacity-60">
                            {section.fields.length} editable fields
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleSave(sectionKey)}
                        disabled={saving[sectionKey] || !hasChanges[sectionKey]}
                        className="gap-2"
                        style={{ backgroundColor: currentAdminTheme.primary }}
                        data-testid={`save-${sectionKey}-btn`}
                      >
                        {saving[sectionKey] ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Save Section
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Fields */}
                    {loading ? (
                      <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: currentAdminTheme.border }} />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {section.fields.map(field => renderField(sectionKey, field))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>

        {/* Help Card */}
        <div 
          className="p-4 rounded-xl flex items-start gap-4"
          style={{ backgroundColor: `${currentAdminTheme.secondary}10`, border: `1px solid ${currentAdminTheme.secondary}30` }}
        >
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: currentAdminTheme.secondary }} />
          <div>
            <p className="font-medium" style={{ color: currentAdminTheme.foreground }}>
              Content changes appear instantly
            </p>
            <p className="text-sm opacity-70 mt-1">
              After saving, your changes will be reflected on the public website immediately. 
              Image URLs should be direct links to images (ending in .jpg, .png, etc.).
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
